// src/pages/Admin/AnnouncementsManager.jsx
// Announcements management for the admin panel: news posts and tournament cards
// on the public Announcements page.
//
// Same philosophy as RosterManager: plain-English labels, a live preview of the
// card as it will appear on the site, and confirmation before anything
// destructive. Publish choices (draft / now / schedule) work identically too,
// so nothing new to learn.

import { useEffect, useMemo, useRef, useState } from "react";
import {
  FaPlus, FaTrash, FaPen, FaCamera, FaSpinner, FaCheck, FaCheckCircle,
  FaClock, FaEyeSlash, FaArrowLeft, FaDownload, FaChevronUp, FaChevronDown,
  FaTrophy, FaCalendar, FaUser, FaStar,
} from "react-icons/fa";
import {
  subscribeToAllAnnouncements, createAnnouncement, updateAnnouncement,
  removeAnnouncement, reorderAnnouncements, seedAnnouncementsFromStaticData,
} from "../../lib/announcementsService";
import { publishLabel } from "../../lib/playersService";
import { uploadImage, formatBytes } from "../../lib/imageUpload";
import { announcementImage } from "../../hooks/useAnnouncements";
import { notifyAnnouncementSaved, notifyAnnouncementRemoved } from "../../lib/notify";

const emptyForm = {
  type: "news",
  title: "", excerpt: "", body: "", date: "", author: "Prime Valor",
  category: "Announcement", eventStatus: "upcoming", prizePool: "", format: "",
  game: "", imageUrl: "", imagePath: "", legacyImageKey: "", featured: false,
  status: "draft", publishAt: null, sortOrder: 0,
};

const input =
  "w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white text-base placeholder-gray-600 focus:outline-none focus:border-red-500 transition-colors";
const labelCls = "block text-sm font-semibold text-gray-300 mb-2";
const hintCls = "text-xs text-gray-500 mt-1.5";

// ─── Card preview ────────────────────────────────────────────────────────────
function CardPreview({ form, previewSrc }) {
  const src = previewSrc || announcementImage(form);
  const isTournament = form.type === "tournament";
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg overflow-hidden w-full max-w-[300px]">
      <div className="aspect-video overflow-hidden bg-gray-800 relative">
        {src ? (
          <img src={src} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-600 text-xs uppercase tracking-widest">
            No image yet
          </div>
        )}
        <div className="absolute top-2 left-2">
          <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase ${
            isTournament
              ? form.eventStatus === "upcoming" ? "bg-green-600 text-white" : "bg-gray-700 text-gray-300"
              : "bg-red-600 text-white"
          }`}>
            {isTournament ? (form.eventStatus || "upcoming") : (form.category || "Announcement")}
          </span>
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-white font-bold text-base line-clamp-2">
          {form.title || "Title goes here"}
        </h3>
        {form.excerpt && (
          <p className="text-gray-400 text-xs mt-2 line-clamp-2">{form.excerpt}</p>
        )}
        {isTournament && (
          <p className="text-gray-400 text-xs mt-2 flex items-center gap-1.5">
            <FaTrophy className="text-red-600" size={10} />
            Prize: {form.prizePool || "TBA"}{form.format ? ` · ${form.format}` : ""}
          </p>
        )}
        <p className="text-gray-500 text-xs mt-3 flex items-center gap-1.5">
          <FaCalendar className="text-red-600" size={10} />{form.date || "Date"}
          {!isTournament && (
            <>
              <FaUser className="text-red-600 ml-2" size={10} />{form.author || "Prime Valor"}
            </>
          )}
        </p>
      </div>
    </div>
  );
}

// ─── Add / edit form ─────────────────────────────────────────────────────────
function AnnouncementForm({ initial, onDone, onCancel }) {
  // publishAt comes out of Firestore as a Timestamp but the form (and the
  // service) work in milliseconds, so convert on the way in — otherwise
  // editing a scheduled item would corrupt its go-live time.
  const [form, setForm] = useState(() =>
    initial
      ? {
          ...initial,
          publishAt:
            typeof initial.publishAt?.toMillis === "function"
              ? initial.publishAt.toMillis()
              : initial.publishAt ?? null,
        }
      : emptyForm
  );
  const [previewSrc, setPreviewSrc] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const fileRef = useRef(null);

  const editingId = initial?.id || null;
  const isEdit = Boolean(editingId);
  const isLiveNow = initial?.status === "published";
  const isTournament = form.type === "tournament";
  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const publishMode =
    form.status !== "published" ? "draft" : form.publishAt ? "schedule" : "now";
  const setPublishMode = (mode) => {
    if (mode === "draft") setForm((f) => ({ ...f, status: "draft", publishAt: null }));
    if (mode === "now") setForm((f) => ({ ...f, status: "published", publishAt: null }));
    if (mode === "schedule") {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(9, 0, 0, 0);
      setForm((f) => ({ ...f, status: "published", publishAt: f.publishAt || tomorrow.getTime() }));
    }
  };

  const handleFile = async (file) => {
    if (!file) return;
    setError(""); setUploading(true); setUploadInfo(null);
    try {
      const localUrl = URL.createObjectURL(file);
      setPreviewSrc(localUrl);
      const result = await uploadImage(file, form.title || "announcement", {
        seed: Date.now(),
        folder: "announcements",
      });
      setForm((f) => ({ ...f, imageUrl: result.url, imagePath: result.path }));
      setUploadInfo({ from: result.originalBytes, to: result.resizedBytes });
    } catch (err) {
      setError(err.message || "Upload failed. Please try again.");
      setPreviewSrc(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError("Please enter a title."); return; }
    setSaving(true); setError("");
    try {
      if (isEdit) await updateAnnouncement(editingId, form);
      else await createAnnouncement(form);
      // Fire-and-forget so a notification problem can never block the save.
      notifyAnnouncementSaved(form);
      onDone();
    } catch (err) {
      setError(err.message || "Could not save. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onCancel} className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white text-sm rounded-lg transition-colors">
          <FaArrowLeft size={12} /> Back
        </button>
        <h2 className="text-xl font-black uppercase tracking-wide text-white">
          {isEdit ? `Edit ${initial.title}` : "Add an announcement"}
        </h2>
      </div>

      {error && (
        <p className="text-red-300 text-sm bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3">{error}</p>
      )}

      <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-start">
        <div className="space-y-6">
          {/* Type — fixed once created so fields can't be orphaned */}
          {!isEdit && (
            <div>
              <label className={labelCls}>What kind of announcement is this?</label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: "news", title: "News post", desc: "An update or announcement with text to read" },
                  { value: "tournament", title: "Tournament", desc: "An event card with prize, format and status" },
                ].map((t) => (
                  <button key={t.value} onClick={() => set("type", t.value)}
                    className={`p-4 rounded-xl border-2 text-left transition-colors ${
                      form.type === t.value ? "border-red-600 bg-red-600/10" : "border-gray-700 hover:border-gray-600"
                    }`}>
                    <p className="text-white font-semibold text-sm">{t.title}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{t.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Image */}
          <div>
            <label className={labelCls}>Image</label>
            <div
              onClick={() => !uploading && fileRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
              className="border-2 border-dashed border-gray-700 hover:border-red-500/60 rounded-xl p-6 text-center cursor-pointer transition-colors"
            >
              {uploading ? (
                <FaSpinner className="animate-spin text-red-500 mx-auto" size={22} />
              ) : (
                <>
                  <FaCamera className="text-gray-600 mx-auto mb-2" size={22} />
                  <p className="text-gray-300 text-sm font-semibold">
                    {form.imageUrl || previewSrc ? "Choose a different image" : "Click to choose an image"}
                  </p>
                  <p className={hintCls}>or drag one here · JPG or PNG</p>
                  <p className="text-xs text-gray-400 mt-2 bg-gray-800/70 inline-block px-3 py-1.5 rounded-lg">
                    Best format: <strong className="text-gray-200">wide (landscape), 16:9</strong> — e.g. 1600 × 900.
                    Portrait images will have their top and bottom cut off.
                  </p>
                </>
              )}
            </div>
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])} />
            {uploadInfo && (
              <p className="text-xs text-green-400 mt-2">
                Image optimised for the web — {formatBytes(uploadInfo.from)} → {formatBytes(uploadInfo.to)}
              </p>
            )}
            {(form.imageUrl || previewSrc) && !uploading && (
              <button
                onClick={() => {
                  setPreviewSrc(null); setUploadInfo(null);
                  setForm((f) => ({ ...f, imageUrl: "", imagePath: "" }));
                }}
                className="mt-2 text-xs text-gray-500 hover:text-red-400 transition-colors"
              >
                Remove this image
              </button>
            )}
          </div>

          {/* Shared basics */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls}>Title *</label>
              <input className={input} value={form.title}
                placeholder={isTournament ? "e.g. 3v3 Tournament" : "e.g. Caribbean Cup 2026 Expands"}
                onChange={(e) => set("title", e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Short summary</label>
              <input className={input} value={form.excerpt}
                placeholder="One or two sentences shown on the card"
                onChange={(e) => set("excerpt", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Date shown on the card</label>
              <input className={input} value={form.date} placeholder="e.g. March 2026"
                onChange={(e) => set("date", e.target.value)} />
              <p className={hintCls}>Just text — write it how you want it to read.</p>
            </div>
          </div>

          {/* News-only fields */}
          {!isTournament && (
            <>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Label on the card</label>
                  <input className={input} value={form.category} placeholder="e.g. Announcement"
                    onChange={(e) => set("category", e.target.value)} />
                </div>
                <div>
                  <label className={labelCls}>Author</label>
                  <input className={input} value={form.author} placeholder="Prime Valor"
                    onChange={(e) => set("author", e.target.value)} />
                </div>
              </div>
              <div>
                <label className={labelCls}>
                  Full text <span className="font-normal text-gray-500">(optional)</span>
                </label>
                <textarea className={`${input} min-h-[140px]`} value={form.body}
                  placeholder="The full announcement. Leave a blank line between paragraphs."
                  onChange={(e) => set("body", e.target.value)} />
                <p className={hintCls}>Shown in full when this post is featured.</p>
              </div>
              <button onClick={() => set("featured", !form.featured)} className="flex items-start gap-3 text-left">
                <span className={`w-5 h-5 mt-0.5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                  form.featured ? "bg-red-600 border-red-600" : "border-gray-600"
                }`}>
                  {form.featured && <FaCheck size={10} className="text-white" />}
                </span>
                <span className="text-sm text-gray-300">
                  Feature this post
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Shows it big at the top of the Announcements page. Only one post can be
                    featured — ticking this takes the spot from the current one automatically.
                  </span>
                </span>
              </button>
            </>
          )}

          {/* Tournament-only fields */}
          {isTournament && (
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Event status</label>
                <select className={input} value={form.eventStatus}
                  onChange={(e) => set("eventStatus", e.target.value)}>
                  <option value="upcoming">Upcoming</option>
                  <option value="completed">Completed</option>
                </select>
                <p className={hintCls}>Upcoming events show a green badge and sort first.</p>
              </div>
              <div>
                <label className={labelCls}>Prize pool</label>
                <input className={input} value={form.prizePool} placeholder="e.g. $1,000 or TBA"
                  onChange={(e) => set("prizePool", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Format</label>
                <input className={input} value={form.format} placeholder="e.g. 3v3"
                  onChange={(e) => set("format", e.target.value)} />
              </div>
              <div>
                <label className={labelCls}>Game</label>
                <input className={input} value={form.game} placeholder="e.g. Call of Duty: Black Ops 7"
                  onChange={(e) => set("game", e.target.value)} />
              </div>
            </div>
          )}

          {/* Publish */}
          <div className="bg-gray-800/40 border border-gray-700 rounded-xl p-5">
            <label className={labelCls}>When should this go live?</label>
            <div className="grid sm:grid-cols-3 gap-3 mt-1">
              {[
                isLiveNow
                  ? { key: "draft", title: "Hide from the site", desc: "Keeps everything, but removes it from the page" }
                  : { key: "draft", title: "Save as a draft", desc: "Not visible on the site yet" },
                { key: "now", title: isLiveNow ? "Keep it live" : "Publish now", desc: "Visible on the site immediately" },
                { key: "schedule", title: "Schedule for later", desc: "Appears automatically on the date you pick" },
              ].map((opt) => (
                <button key={opt.key} onClick={() => setPublishMode(opt.key)}
                  className={`p-4 rounded-xl border-2 text-left transition-colors ${
                    publishMode === opt.key ? "border-red-600 bg-red-600/10" : "border-gray-700 hover:border-gray-600"
                  }`}>
                  <p className="text-white font-semibold text-sm">{opt.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
                </button>
              ))}
            </div>
            {publishMode === "schedule" && (
              <div className="mt-3">
                <input type="datetime-local" className={input}
                  value={form.publishAt ? new Date(form.publishAt - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ""}
                  onChange={(e) => set("publishAt", e.target.value ? new Date(e.target.value).getTime() : null)} />
              </div>
            )}
          </div>

          {/* Save */}
          <div className="flex gap-3">
            <button onClick={handleSave} disabled={saving || uploading}
              className="flex-1 py-4 bg-red-600 hover:bg-red-700 disabled:bg-red-900 disabled:cursor-not-allowed text-white font-bold uppercase tracking-wide rounded-lg transition-colors">
              {saving ? "Saving…" : isEdit ? "Save changes" : "Add announcement"}
            </button>
            <button onClick={onCancel} className="px-6 py-4 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors">
              Cancel
            </button>
          </div>
        </div>

        {/* Live preview */}
        <div className="lg:sticky lg:top-24">
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-3">How it will look</p>
          <CardPreview form={form} previewSrc={previewSrc} />
        </div>
      </div>
    </div>
  );
}

// ─── List ────────────────────────────────────────────────────────────────────
function AnnouncementsManager() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // item object or "new"
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => subscribeToAllAnnouncements(setItems), []);

  const flash = (msg) => { setNotice(msg); setTimeout(() => setNotice(""), 4000); };

  const handleImport = async () => {
    if (!window.confirm("Import the existing announcements into the new system? This only needs doing once.")) return;
    setBusy(true);
    try {
      const count = await seedAnnouncementsFromStaticData();
      flash(`Imported ${count} announcements.`);
    } catch (err) {
      flash(err.message);
    } finally { setBusy(false); }
  };

  const handleDelete = async (item) => {
    setBusy(true);
    try {
      await removeAnnouncement(item);
      notifyAnnouncementRemoved(item);
      flash(`"${item.title}" removed.`);
      setConfirmDelete(null);
    } catch (err) { flash(err.message); }
    finally { setBusy(false); }
  };

  const sections = useMemo(() => {
    const list = items || [];
    return [
      { key: "news", label: "News", items: list.filter((i) => i.type === "news") },
      { key: "tournament", label: "Tournaments", items: list.filter((i) => i.type === "tournament") },
    ];
  }, [items]);

  const handleMove = async (sectionItems, index, direction) => {
    const list = [...sectionItems];
    const target = index + direction;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    setBusy(true);
    try {
      await reorderAnnouncements(list);
    } catch (err) {
      flash(err.message || "Could not save the new order.");
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <AnnouncementForm
        initial={editing === "new" ? null : editing}
        onDone={() => { setEditing(null); flash("Saved."); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="space-y-6">
      {notice && (
        <p className="text-sm bg-green-600/15 border border-green-600/40 text-green-300 rounded-lg px-4 py-3">{notice}</p>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black uppercase tracking-wide text-white">Announcements</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            News posts and tournament cards on the Announcements page. Changes go straight to the website.
          </p>
        </div>
        <button onClick={() => setEditing("new")}
          className="flex items-center gap-2 px-5 py-3 bg-red-600 hover:bg-red-700 text-white font-bold uppercase text-sm rounded-lg transition-colors">
          <FaPlus size={12} /> Add an announcement
        </button>
      </div>

      {items && items.length === 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 text-center space-y-3">
          <p className="text-gray-300">
            The announcements currently on the website haven't been brought into this system yet.
          </p>
          <button onClick={handleImport} disabled={busy}
            className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white font-bold uppercase rounded-lg transition-colors">
            <FaDownload size={13} /> {busy ? "Importing…" : "Import existing announcements"}
          </button>
        </div>
      )}

      {sections.map(({ key, label, items: list }) =>
        list.length === 0 ? null : (
          <div key={key}>
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3 border-l-2 border-red-600 pl-3">
              {label} · {list.length}
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {list.map((item, index) => {
                const status = publishLabel(item);
                const img = announcementImage(item);
                return (
                  <div key={item.id} className="flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl p-3">
                    {list.length > 1 && (
                      <div className="flex flex-col flex-shrink-0 -my-1">
                        <button onClick={() => handleMove(list, index, -1)} disabled={index === 0 || busy}
                          aria-label={`Move ${item.title} up`}
                          className="p-1 text-gray-600 hover:text-white disabled:opacity-25 disabled:hover:text-gray-600 transition-colors">
                          <FaChevronUp size={10} />
                        </button>
                        <button onClick={() => handleMove(list, index, 1)} disabled={index === list.length - 1 || busy}
                          aria-label={`Move ${item.title} down`}
                          className="p-1 text-gray-600 hover:text-white disabled:opacity-25 disabled:hover:text-gray-600 transition-colors">
                          <FaChevronDown size={10} />
                        </button>
                      </div>
                    )}
                    <div className="w-20 h-12 rounded-lg overflow-hidden bg-gray-800 flex-shrink-0">
                      {img ? (
                        <img src={img} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-red-600/40 font-black">
                          {item.title?.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-semibold truncate text-sm">{item.title}</p>
                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold mt-0.5 ${
                        status === "Live" ? "text-green-400"
                          : status === "Draft" || status === "Hidden" ? "text-gray-500"
                          : "text-amber-400"
                      }`}>
                        {status === "Live" ? <FaCheckCircle size={9} />
                          : status === "Draft" || status === "Hidden" ? <FaEyeSlash size={9} />
                          : <FaClock size={9} />}
                        {status}
                        {item.featured && (
                          <span className="inline-flex items-center gap-1 text-red-400 ml-2">
                            <FaStar size={9} /> Featured
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex gap-1 flex-shrink-0">
                      <button onClick={() => setEditing(item)} className="p-2 text-gray-500 hover:text-white transition-colors" aria-label="Edit">
                        <FaPen size={12} />
                      </button>
                      <button onClick={() => setConfirmDelete(item)} className="p-2 text-gray-600 hover:text-red-500 transition-colors" aria-label="Remove">
                        <FaTrash size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 max-w-sm w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold text-lg">Remove "{confirmDelete.title}"?</h3>
            <p className="text-gray-400 text-sm">
              It'll be taken off the site straight away. This can't be undone.
            </p>
            <div className="flex gap-2">
              <button onClick={() => handleDelete(confirmDelete)} disabled={busy}
                className="flex-1 py-3 bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white font-bold uppercase text-sm rounded-lg transition-colors">
                {busy ? "Removing…" : "Yes, remove"}
              </button>
              <button onClick={() => setConfirmDelete(null)}
                className="flex-1 py-3 bg-gray-800 hover:bg-gray-700 text-gray-300 font-bold uppercase text-sm rounded-lg transition-colors">
                Keep
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AnnouncementsManager;
